import {
  AlertCircle,
  Check,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserRoundCog,
  UserX,
  X,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { authErrorMessage } from '../auth/auth-errors';
import { useAuth } from '../auth/useAuth';
import { requireFirebaseFunctions } from '../lib/firebase';
import type { ManagedUser, UserRole, UsersPayload } from '../types/auth';

const roleLabels: Record<UserRole, string> = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  utente: 'Utente',
};

type EditorState =
  | { mode: 'create' }
  | { mode: 'edit'; user: ManagedUser }
  | { mode: 'password'; user: ManagedUser }
  | null;

function formatDate(value: string | null): string {
  if (!value) return 'Mai';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? 'Mai'
    : new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function UserEditor({
  state,
  busy,
  onClose,
  onSubmit,
}: {
  state: Exclude<EditorState, null>;
  busy: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => Promise<void>;
}) {
  const user = state.mode === 'create' ? null : state.user;
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<UserRole>(user?.role ?? 'utente');
  const [password, setPassword] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (state.mode === 'password') {
      void onSubmit({ uid: state.user.uid, password });
      return;
    }
    void onSubmit({
      ...(user ? { uid: user.uid } : {}),
      firstName,
      lastName,
      email,
      role,
      ...(state.mode === 'create' ? { password } : {}),
    });
  };

  const title = state.mode === 'create'
    ? 'Crea utente locale'
    : state.mode === 'password'
      ? 'Reimposta password'
      : 'Modifica utente';

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.currentTarget === event.target && !busy) onClose();
    }}>
      <section className="user-modal" role="dialog" aria-modal="true" aria-labelledby="user-modal-title">
        <header>
          <div>
            <p>Gestione accesso</p>
            <h2 id="user-modal-title">{title}</h2>
          </div>
          <button type="button" aria-label="Chiudi" onClick={onClose} disabled={busy}><X size={19} /></button>
        </header>
        <form onSubmit={submit}>
          {state.mode === 'password' ? (
            <>
              <div className="user-modal-subject">
                <span><KeyRound size={18} /></span>
                <div><strong>{user?.displayName}</strong><small>{user?.email}</small></div>
              </div>
              <label className="field">
                <span>Nuova password</span>
                <input type="password" minLength={12} maxLength={128} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required autoFocus />
              </label>
              <p className="password-policy">Almeno 12 caratteri, con maiuscole, minuscole, un numero e un simbolo.</p>
            </>
          ) : (
            <>
              <div className="user-form-grid">
                <label className="field">
                  <span>Nome</span>
                  <input value={firstName} onChange={(event) => setFirstName(event.target.value)} required autoFocus />
                </label>
                <label className="field">
                  <span>Cognome</span>
                  <input value={lastName} onChange={(event) => setLastName(event.target.value)} required />
                </label>
              </div>
              <label className="field">
                <span>Email</span>
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={state.mode === 'edit'} />
              </label>
              {state.mode === 'create' && (
                <>
                  <label className="field">
                    <span>Password iniziale</span>
                    <input type="password" minLength={12} maxLength={128} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
                  </label>
                  <p className="password-policy">Almeno 12 caratteri, con maiuscole, minuscole, un numero e un simbolo.</p>
                </>
              )}
              <label className="field">
                <span>Ruolo nel prodotto</span>
                <select value={role} onChange={(event) => setRole(event.target.value as UserRole)}>
                  <option value="utente">Utente</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </label>
            </>
          )}
          <footer>
            <button className="button button-secondary" type="button" onClick={onClose} disabled={busy}>Annulla</button>
            <button className="button button-primary" type="submit" disabled={busy}>
              {busy ? <><LoaderCircle className="spin" size={16} /> Salvataggio…</> : <><Check size={16} /> Salva</>}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

export function UsersPage({ embedded = false }: { embedded?: boolean }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [role, setRole] = useState('all');
  const [status, setStatus] = useState('all');
  const canView = currentUser?.role === 'superadmin';
  const canManage = currentUser?.role === 'superadmin';

  const loadUsers = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    if (currentUser?.demo) {
      setUsers([{
        uid: currentUser.uid,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        displayName: currentUser.displayName,
        role: currentUser.role,
        source: 'local',
        hasLocalProvider: true,
        hasJustbitProvider: false,
        justbitRole: null,
        active: true,
        disabled: false,
        lastSignInAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      }]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const callable = httpsCallable<void, UsersPayload>(requireFirebaseFunctions(), 'listUsers');
      setUsers((await callable()).data.users);
    } catch (loadError) {
      setError(authErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [canView, currentUser]);

  useEffect(() => {
    const timer = window.setTimeout(() => { void loadUsers(); }, 0);
    return () => window.clearTimeout(timer);
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('it');
    return users.filter((managedUser) => {
      const matchesSearch = !query || [managedUser.firstName, managedUser.lastName, managedUser.email]
        .some((value) => value.toLocaleLowerCase('it').includes(query));
      return matchesSearch
        && (source === 'all' || managedUser.source === source)
        && (role === 'all' || managedUser.role === role)
        && (status === 'all' || (status === 'active') === managedUser.active);
    });
  }, [role, search, source, status, users]);

  const runAction = async (name: string, data: unknown, uid = 'editor') => {
    setBusyUid(uid);
    setError(null);
    try {
      const callable = httpsCallable(requireFirebaseFunctions(), name);
      await callable(data);
      setEditor(null);
      await loadUsers();
    } catch (actionError) {
      setError(authErrorMessage(actionError));
    } finally {
      setBusyUid(null);
    }
  };

  if (!canView) {
    const AccessRoot = embedded ? 'section' : 'main';
    return (
      <AccessRoot className="placeholder-page">
        <section className="placeholder-card">
          <span className="placeholder-icon"><LockKeyhole size={24} /></span>
          <p className="app-eyebrow">Accesso riservato</p>
          <h1>Gestione utenti</h1>
          <p>La lista degli utenti è disponibile solo ad Admin e Superadmin.</p>
        </section>
      </AccessRoot>
    );
  }

  const Root = embedded ? 'section' : 'main';

  return (
    <Root className={`${embedded ? 'settings-tab-panel ' : 'app-content '}users-page`}>
      <header className="page-heading users-heading">
        <div>
          <p className="app-eyebrow">Amministrazione</p>
          {embedded ? <h2>Utenti e accessi</h2> : <h1>Utenti e accessi</h1>}
          <p>Identità, ruoli locali e stato degli account che possono usare Talos.</p>
        </div>
        {canManage ? (
          <button className="button button-primary" type="button" onClick={() => setEditor({ mode: 'create' })}>
            <Plus size={17} /> Aggiungi utente
          </button>
        ) : (
          <span className="readonly-chip"><ShieldCheck size={15} /> Sola lettura</span>
        )}
      </header>

      <section className="users-toolbar" aria-label="Filtri utenti">
        <label className="users-search"><Search size={16} /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca nome o email…" /></label>
        <div>
          <label><span>Provenienza</span><select value={source} onChange={(event) => setSource(event.target.value)}><option value="all">Tutte</option><option value="justbit">Justbit</option><option value="local">Locale</option></select></label>
          <label><span>Ruolo</span><select value={role} onChange={(event) => setRole(event.target.value)}><option value="all">Tutti</option><option value="superadmin">Superadmin</option><option value="admin">Admin</option><option value="utente">Utente</option></select></label>
          <label><span>Stato</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">Tutti</option><option value="active">Attivi</option><option value="disabled">Disabilitati</option></select></label>
        </div>
      </section>

      {error && <div className="users-feedback" role="alert"><AlertCircle size={17} /><span>{error}</span><button type="button" onClick={() => setError(null)} aria-label="Chiudi"><X size={15} /></button></div>}

      <section className="users-table-card">
        <header><div><strong>{filteredUsers.length}</strong><span>{filteredUsers.length === 1 ? 'utente visibile' : 'utenti visibili'}</span></div><small>Ordinati per ultimo accesso</small></header>
        {loading ? (
          <div className="users-loading"><LoaderCircle className="spin" size={22} /><span>Caricamento utenti…</span></div>
        ) : filteredUsers.length === 0 ? (
          <div className="users-empty"><UserRoundCog size={25} /><strong>Nessun utente corrisponde ai filtri</strong><span>Modifica la ricerca o rimuovi uno dei filtri.</span></div>
        ) : (
          <div className="users-table-scroll">
            <table className="users-table">
              <thead><tr><th>Nome e cognome</th><th>Provenienza</th><th>Ruolo</th><th>Ruolo Justbit</th><th>Stato</th><th>Ultimo accesso</th>{canManage && <th><span className="sr-only">Azioni</span></th>}</tr></thead>
              <tbody>
                {filteredUsers.map((managedUser) => (
                  <tr key={managedUser.uid}>
                    <th scope="row"><span className="user-avatar">{managedUser.firstName[0] || managedUser.email[0]}{managedUser.lastName[0] || ''}</span><span><strong>{managedUser.displayName}</strong><small>{managedUser.email}</small></span></th>
                    <td><span className={`source-chip ${managedUser.source}`}>{managedUser.source === 'justbit' ? 'Justbit' : 'Locale'}</span>{managedUser.hasJustbitProvider && managedUser.hasLocalProvider && <small className="provider-detail">anche credenziali locali</small>}</td>
                    <td><span className={`role-chip ${managedUser.role}`}>{roleLabels[managedUser.role]}</span></td>
                    <td>{managedUser.justbitRole || <span className="empty-value">—</span>}</td>
                    <td><span className={`status-chip ${managedUser.active ? 'active' : 'disabled'}`}><i />{managedUser.active ? 'Attivo' : 'Disabilitato'}</span></td>
                    <td><time dateTime={managedUser.lastSignInAt ?? undefined}>{formatDate(managedUser.lastSignInAt)}</time></td>
                    {canManage && (
                      <td>
                        <div className="user-actions">
                          <button type="button" title="Modifica utente" aria-label={`Modifica ${managedUser.displayName}`} onClick={() => setEditor({ mode: 'edit', user: managedUser })}><Pencil size={15} /></button>
                          {managedUser.hasLocalProvider && <button type="button" title="Reimposta password" aria-label={`Reimposta password di ${managedUser.displayName}`} onClick={() => setEditor({ mode: 'password', user: managedUser })}><KeyRound size={15} /></button>}
                          <button
                            type="button"
                            title={managedUser.active ? 'Disabilita' : 'Riabilita'}
                            aria-label={`${managedUser.active ? 'Disabilita' : 'Riabilita'} ${managedUser.displayName}`}
                            disabled={busyUid === managedUser.uid}
                            onClick={() => void runAction('setManagedUserStatus', { uid: managedUser.uid, active: !managedUser.active }, managedUser.uid)}
                          >
                            {busyUid === managedUser.uid ? <LoaderCircle className="spin" size={15} /> : managedUser.active ? <UserX size={15} /> : <UserCheck size={15} />}
                          </button>
                          <button
                            className="danger"
                            type="button"
                            title="Elimina dal prodotto"
                            aria-label={`Elimina ${managedUser.displayName} dal prodotto`}
                            disabled={busyUid === managedUser.uid}
                            onClick={() => {
                              if (window.confirm(`Eliminare ${managedUser.displayName} da Talos?`)) {
                                void runAction('deleteManagedUser', { uid: managedUser.uid }, managedUser.uid);
                              }
                            }}
                          ><Trash2 size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editor && (
        <UserEditor
          key={`${editor.mode}-${editor.mode === 'create' ? 'new' : editor.user.uid}`}
          state={editor}
          busy={busyUid === 'editor'}
          onClose={() => setEditor(null)}
          onSubmit={async (data) => {
            const functionName = editor.mode === 'create'
              ? 'createLocalUser'
              : editor.mode === 'password'
                ? 'resetLocalUserPassword'
                : 'updateManagedUser';
            await runAction(functionName, data);
          }}
        />
      )}
    </Root>
  );
}
